import React from 'react'
import { CloudPartnerDetails } from './CloudPartnerDetails/CloudPartnerDetails'
import { Sections } from '../../modules/flyOutOnboarding/reducer'
import { FormikProps } from 'formik'
import { FlyOutArnDetails } from './FlyOutArnDetails/FlyOutArnDetails'
import { useStyles } from './FlyOutCloudSettings.styles'
import { FormLockedType } from '../../types/FormLockedType'
import { CloudPartnerDetailsWrapper } from './CloudPartnerDetailsWrapper/CloudPartnerDetailsWrapper'
import { useFlagValue } from '@labrav/flags'
import { FlyoutCasProgramSetupWrapper } from './FlyoutCasProgramSetupWrapper/FlyoutCasProgramSetupWrapper'
import { FlyoutCasProgramSetup } from './FlyoutCasProgramSetup/FlyoutCasProgramSetup'
import {
  FlyOutArnAndDataFeedsWrapper,
  StackDeploymentType,
} from './FlyOutArnAndDataFeedsWrapper/FlyOutArnAndDataFeedsWrapper'
import { FlyOutArnDetailsWrapper } from './FlyOutArnDetailsWrapper/FlyOutArnDetailsWrapper'
import { useAccountId } from '@labrav/common'
export type FlyOutCloudSettingsProps = {
  cloudSettings: FormikProps<Sections>
}
export const FlyOutCloudSettings: React.FC<
  FlyOutCloudSettingsProps & FormLockedType
> = ({ cloudSettings, isFormLocked }) => {
  const { classes } = useStyles()
  const partnerId = useAccountId({ product: 'flyout' }) ?? ''
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )

  const ownBucketMode: 'yes' | 'no' =
    cloudSettings.values.cloudSettings?.isOwnS3Bucket === true ? 'yes' : 'no'

  const stackType: StackDeploymentType =
    isOnboardingImprovementsEnabled &&
    cloudSettings.values.cloudSettings?.stackType === 'terraform'
      ? 'terraform'
      : 'cloudformation'

  const applyDataFeedsStackFieldReset = (nextMode: 'yes' | 'no') => {
    void cloudSettings.setFieldValue(
      'cloudSettings.isOwnS3Bucket',
      nextMode === 'yes'
    )
    void cloudSettings.setFieldValue(
      'cloudSettings.automation',
      nextMode === 'no'
    )
    void cloudSettings.setFieldValue('cloudSettings.cloudRoleArn', '')
    void cloudSettings.setFieldValue('cloudSettings.cloudCasS3BucketName', '')
    void cloudSettings.setFieldValue('cloudSettings.cloudKmsKeyArn', '')
    void cloudSettings.setFieldValue('cloudSettings.cloudCasSnsTopic', '')
    void cloudSettings.setFieldValue('cloudSettings.connectionTested', false)
    void cloudSettings.setFieldValue(
      'cloudSettings.dataFeedsConnectionTested',
      false
    )
    void cloudSettings.setFieldValue(
      'cloudSettings.testConnectionStatus',
      'standBy'
    )
    void cloudSettings.setFieldValue(
      'cloudSettings.terraformConnectionValidated',
      false
    )
    if (typeof cloudSettings.setFieldError === 'function') {
      void cloudSettings.setFieldError(
        'cloudSettings.terraformConnectionValidated',
        undefined
      )
      void cloudSettings.setFieldError(
        'cloudSettings.dataFeedsConnectionTested',
        undefined
      )
    }
    if (typeof cloudSettings.setFieldTouched === 'function') {
      void cloudSettings.setFieldTouched(
        'cloudSettings.terraformConnectionValidated',
        false,
        false
      )
      void cloudSettings.setFieldTouched(
        'cloudSettings.cloudRoleArn',
        false,
        false
      )
      void cloudSettings.setFieldTouched(
        'cloudSettings.cloudCasS3BucketName',
        false,
        false
      )
      void cloudSettings.setFieldTouched(
        'cloudSettings.cloudKmsKeyArn',
        false,
        false
      )
      void cloudSettings.setFieldTouched(
        'cloudSettings.cloudCasSnsTopic',
        false,
        false
      )
    }
  }

  const handleStackTypeChange = (value: StackDeploymentType) => {
    if (value === stackType) {
      return
    }
    void cloudSettings.setFieldValue('cloudSettings.stackType', value)
  }

  const handleDataFeedsBucketChoiceChange = (value: 'yes' | 'no') => {
    const nextMode = value === 'yes' ? 'yes' : 'no'
    if (nextMode === ownBucketMode) {
      return
    }
    applyDataFeedsStackFieldReset(nextMode)
  }

  return (
    <div className={classes.container}>
      <CloudPartnerDetailsWrapper
        children={
          <CloudPartnerDetails
            cloudSettings={cloudSettings}
            isFormLocked={isFormLocked}
          />
        }
      />
      {isMarketplaceCloudSettingsRevampEnabled ? (
        <FlyOutArnAndDataFeedsWrapper
          stackType={stackType}
          onStackTypeChange={handleStackTypeChange}
          dataFeedsInOwnS3Bucket={ownBucketMode}
          onDataFeedsInOwnS3BucketChange={handleDataFeedsBucketChoiceChange}
          awsAccountId={cloudSettings.values.cloudSettings?.cloudAccountId}
          awsAccountName={cloudSettings.values.cloudSettings?.cloudAccountName}
          clientExternalId={
            cloudSettings.values.cloudSettings?.cloudExternalId ?? partnerId
          }
          arnChildren={
            <FlyOutArnDetails
              key={`${ownBucketMode}-${stackType}`}
              isFormLocked={isFormLocked}
              showConnectionStatus={ownBucketMode === 'yes'}
            />
          }
          dataFeedsChildren={
            <FlyoutCasProgramSetup
              key={`${ownBucketMode}-${stackType}`}
              isFormLocked={isFormLocked}
              showCloudFormationDataFeedsSetup={ownBucketMode === 'yes'}
              enableDataFeedsRevampBehavior={Boolean(
                isMarketplaceCloudSettingsRevampEnabled
              )}
            />
          }
        />
      ) : (
        <>
          <FlyOutArnDetailsWrapper
            children={<FlyOutArnDetails isFormLocked={isFormLocked} />}
          />
          <FlyoutCasProgramSetupWrapper
            children={
              <FlyoutCasProgramSetup
                isFormLocked={isFormLocked}
                enableDataFeedsRevampBehavior={false}
              />
            }
          />
        </>
      )}
    </div>
  )
}
